<template>

    <div class="relative">
        <div class="form-control flex justify-between gap-2" :class="selectedItemLabelClasses" v-if="selectedItem">
            <button @click.prevent="focus" class="truncate">
                {{ selectedItemLabel }}
            </button>

            <button @click.prevent="clear" class="text-gray-400 hover:text-brand transition duration-300">
                <i class="mdi mdi-close-circle-outline"></i>
            </button>
        </div>

        <input class="form-control truncate"
               :class="inputClasses"
               v-if="!selectedItem"
               ref="input"
               type="text"
               @focus="focus"
               @click="setFocusOnInput"
               @keyup.down.prevent="selectNextItem"
               @keyup.up.prevent="selectPreviousItem"
               @keyup.enter.prevent="selectCurrentItem"
               :placeholder="placeholder"
               :disable="selectedItem"
               v-model="inputText">

        <transition name="fade">
            <div v-if="showItemsPopover" :class="listCustomClass ? listCustomClass : 'min-w-full'" :style="listCustomStyle"
                 class="popover bg-white overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100">
                <ul class="itemsList">

                    <li v-if="!loading && items.length === 0 && inputText.length>0" class="el-select-dropdown__item">No Results Matching "{{ inputText }}"</li>
                    <li v-if="loading && items.length === 0" class="el-select-dropdown__item">Searching...</li>
                    <li v-if="!loading && items.length === 0 && inputText.length === 0" class="el-select-dropdown__item">No results found</li>

                    <li v-for="(item,index) in items"
                        class="el-select-dropdown__item h-auto "
                        :class="{'selected hover': index === searchSelection}"
                        @click="select(item)">
                        <slot :item="item" ></slot>
                    </li>
                </ul>
            </div>
        </transition>

        <!-- Fullscreen button - used for moving focus from input while still handling click from popover list item -->
        <button style="inset: 0; position: fixed; top:0; left:0; width: 100%; height: 100%; background: transparent; border:none;" v-if="showItemsPopover"
                @click.prevent="hidePopover">
        </button>
    </div>
</template>

<script>
export default {
    name: "RemoteSelect",

    props: {
        value: {},
        data: {},
        itemLabel: {},
        itemKey: {},
        loading: {},
        remoteMethod: {},
        placeholder: {},

        inputClasses: {
            required: false,
            default: "h-9",
            type: String,
            Description: "The classes for the input part of the component"
        },
        selectedItemLabelClasses: {
            required: false,
            default: "text-base",
            type: String,
            Description: "The classes for the selected item label"
        },
        ignoreUserPortfolioAccess: {
            type: Boolean,
            default: true,
            required: false,
            description: "If false, checks if the user has portfolio access before enabling options; if true, skips this check and enables all options."
        },
        listCustomClass: {
            required: false,
            default: null,
            type: String,
            description: "If you want to add custom tailwind classes to the response list"
        },
        listCustomStyle: {
            required: false,
            default: null,
            type: String,
            description: "If you want to add custom styling to the response list"
        }
    },

    data () {
        return {
            inputText: "",
            items: this.data,
            selectedItem: this.value,
            showLoading: this.showLoading,
            showItemsPopover: false,
            searchSelection: 0
        };
    },

    watch: {
        data (newValue) {
            this.items = newValue;
            this.searchSelection = 0;
        },

        inputText () {
            this.inputChanged();
        },

        value (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.selectedItem = newValue;
            }
        }
    },

    computed: {
        selectedItemLabel () {
            this.showItemsPopover = false;

            if (this.selectedItem) {
                return this.selectedItem[this.itemLabel];
            }

            return "";
        },
    },

    methods: {
        setFocusOnInput () {
            if (this.$refs.input) {
                this.$refs.input.focus();
            }
        },

        focus () {
            this.showItemsPopover = true;
            this.$emit("focus");
            this.setFocusOnInput();
        },

        hidePopover () {
            this.showItemsPopover = false;
            this.$emit("clicked-outside", this.showItemsPopover);
        },

        inputChanged: _.debounce(function () {

            if (this.selectedItem && this.inputText !== this.selectedItemLabel) {
                this.selectedItem = null;
            }

            if (!this.showItemsPopover && this.selectedItem == null) {
                this.showItemsPopover = true;
            }

            if (this.selectedItem == null && this.inputText !== "") {
                this.remoteMethod(this.inputText);
            } else {
                this.showItemsPopover = false;
            }

        }, 300),

        select (item) {
            if (item.hasOwnProperty("user_portfolio_access") && !item.user_portfolio_access && !this.ignoreUserPortfolioAccess) {
                return;
            }

            this.selectedItem = item;

            if (this.itemKey) {
                this.$emit("input", item[this.itemKey]);
            } else {
                this.$emit("input", item);
            }
        },

        selectNextItem () {
            if ((this.searchSelection + 1) < this.items.length) {
                this.searchSelection++;
            }
        },

        selectPreviousItem () {
            if ((this.searchSelection - 1) >= 0) {
                this.searchSelection--;
            }
        },

        selectCurrentItem () {
            if (this.items.length > this.searchSelection) {
                this.select(this.items[this.searchSelection]);
            }
        },

        clear () {
            this.selectedItem = null;
            this.clearInput();
            this.$emit("input", null);
        },

        clearInput () {
            this.inputText = "";
        },
    },
};
</script>

<style scoped>
.popover {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2001;
    max-width: 500px;
    border: solid 1px #E4E7ED;
    border-radius: 4px;
    max-height: 400px;
    background-color: #FFFFFF;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    overflow-y: auto !important;
    margin-top: 3rem;
    margin-left: 0.25rem;
    margin-right: 0.25rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
}

.itemsList {
    list-style: none;
    padding: 0;
    margin: 0;
}

</style>
